import { LitElement, html, nothing } from 'lit';
import typographyStyles from '@cof/fs-typography-core/lib/typography.lit.js';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { fs24Edit, fs24Tag } from '@cof/fs-icons';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { style } from './offer.component.style.js';
import { DebugService } from '../../services/debug/debug.service.js';
import '@cof/fs-typography-wc/fs-heading.js';
import '@cof/fs-typography-wc/fs-body-text.js';
import '@cof/fs-text-field-wc/fs-text-field.js';
import '@cof/fs-helper-text-wc/fs-helper-text.js';
import '@cof/fs-circular-loader-wc/fs-circular-loader.js';
import '@cof/fs-container-wc/fs-container.js';
import './header-message/offer-header-message.component.js';
import { connectedEntryDefault } from './assets/connected-entry-default.js';
import { connectedEntryNoMatch } from './assets/connected-entry-no-match.js';
import { connectedEntrySecureLogin } from './assets/connected-entry-secure-login.js';
import { connectedEntrySystemFailure } from './assets/connected-entry-system-failure.js';
import { CustomerOffersService } from '../../services/customer-offers/customer-offers.service.js';
import { MatchingState } from '../../models/search-customer-offers.model.js';
import SessionService from '../../services/session/session.service.js';
import { stratum } from '../../constants/stratum.js';
import { QUERY_PARAMS } from '../../constants/login.constants.js';
import { OFFER_CONST, CODELESS_ENTRY_CONST } from '../../constants/constants.js';
import SsoService from '../../services/sso/sso.service.js';
import ReturnCustValidationService from '../../services/return-cust-validation/return-cust-validation.service.js';
import { KochavaService } from '../../services/kochava/kochava.service.js';

class RefiOffer extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('OfferComponent');
    this.promotionalCode = '';
    this.tax = '';
    this.errorMessage = '';
    this.editAccessCodeInputFormAppears = '';
    this.userUpdatedAccessCode = '';
    this.headerAprValue = '';
    this.enteredAccessCodeInput = '';
    this.routedWithExternalId = '';
    this.eligibleForAccessCodeEaseLogin = '';
    const icons = [fs24Edit, fs24Tag];
    for (const icon of icons) {
      FsIcon.add(icon);
    }
    this.clickSSNTagSent = false;
    this.clickAccessCodeTagSent = false;
    this.connectedEntryAccessCodePrefilled = false;
  }

  static properties = {
    promotionalCode: { type: String },
    tax: { type: String },
    loading: { type: Boolean },
    errorMessage: { type: String },
    errorHeading: { type: String, reflect: true },
    editAccessCodeInputFormAppears: { type: String },
    featureToggles: { type: Object, attribute: 'feature-toggles' },
  };

  static styles = [style, typographyStyles];

  connectedCallback() {
    super.connectedCallback();
    sessionStorage.setItem('auto-refi-used-offer-page', 'true');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  render() {
    return html`
      <refi-track-pageview pagename="host-app:access code"></refi-track-pageview>
      <div class="offer-page">
        ${this.connectedEntryLogo()}
        ${this.loading
          ? this.renderOfferMatchTransition()
          : html`
              <div class="connected-entry-header">${this.errorMessage ? this.errorHeader() : this.headerAprValue ? this.prefillAccessCodeHeader() : this.noPrefillAccessCodeHeader()}</div>
              ${this.displayAccessCodeForm(this.errorMessage)}
              <div class="button continue-without-code-button">
                <div><fs-button ghost href="#" data-track="apply without access code" @click="${this.clearOffer}">${OFFER_CONST.CONTINUE_WITHOUT_ACCESS_CODE}</fs-button></div>
              </div>
            `}
      </div>
    `;
  }

  // Logo rendered 1/4: Default, No Match, System Failure, Match Transition
  connectedEntryLogo() {
    return html` <div class="connected-entry-logo">${this.errorMessage ? this.displayErrorConnectedEntryLogo(this.errorMessage) : this.loading ? html`${connectedEntrySecureLogin}` : html`${connectedEntryDefault}`}</div> `;
  }

  // Header Messages 1/3: Prefill Promo, No Promo, No Match
  renderHeaderMessage(message, heading) {
    return html`<offer-header-message message="${message}" heading="${heading}"></offer-header-message>`;
  }

  // Header Message: System failure
  errorHeader() {
    return this.renderHeaderMessage(this.errorMessage, this.errorHeading);
  }

  prefillAccessCodeHeader() {
    return this.renderHeaderMessage(OFFER_CONST.CONNECTED_ENTRY_DEFAULT_MESSAGE, `Let’s match you to your ${this.headerAprValue}% offer`);
  }

  noPrefillAccessCodeHeader() {
    return this.renderHeaderMessage(OFFER_CONST.CONNECTED_ENTRY_DEFAULT_MESSAGE, OFFER_CONST.CONNECTED_ENTRY_NO_PREFILL_HEADER);
  }

  // Offer Match Transition Screen with spinner
  // (02/05/25 TBD: Should this be animation? Current Behavior: Success spinner renders for 1 sec > route to CI login)
  renderOfferMatchTransition() {
    return html`
      <div class="connected-entry-header">
        <fs-heading class="title-text connected-entry-successful" size="large">${OFFER_CONST.CONNECTED_ENTRY_MATCH_FOUND_HEADER}</fs-heading>
        <div class="spinner-container">
          <fs-circular-loader size="large" accessibility-label="Loader"></fs-circular-loader>
        </div>
      </div>
    `;
  }

  // Display Access Code Form 1/4: Prefill promo, No promo, Edit prefill promo, No Access Code Form (system failure)
  displayAccessCodeForm(errorMessage) {
    if (errorMessage === OFFER_CONST.CONNECTED_ENTRY_FAILED_MESSAGE) {
      return nothing;
    }
    return html` <form class="cont-connected-entry" name="promoCodeForm" @submit="${this.searchOffer}">${this.renderAccessCodeField()} ${this.renderSSNField()} ${this.findMySpecialOfferButton()}</form>`;
  }

  renderAccessCodeField() {
    return html` <div class="form-field">${this.editAccessCodeInputFormAppears ? this.editAccessCodeForm() : this.routedWithExternalId ? this.prefilledAccessCode() : this.noPrefillAccessCode()}</div> `;
  }

  // Access Code Field flavors 1/3: Prefill promo, No Prefill promo, Edit Prefill promo
  noPrefillAccessCode() {
    return html` <div class="noPrefillAccessCode">
      <div>
        <fs-text-field
          class="includes-error-message enter-access-code-field"
          id="promotionalCode"
          name="promotionalCode"
          type="text"
          value-missing-message="Please enter your access code"
          maxLength="8"
          minLength="8"
          too-short-message="Your access code needs to have 8 numbers"
          pattern-mismatch-message="Only numbers are allowed in this field"
          pattern="\\d{8}"
          @click="${this.accessCodeFieldClicked}"
          @input="${ev => this.changed('promotionalCode', ev)}"
          value="${this.promotionalCode}"
          required
          >Enter your access code
        </fs-text-field>
      </div>
    </div>`;
  }

  prefilledAccessCode() {
    return html` <fs-container border="" elevation="0" radius="large">
      <div class="wrapper">
        <div class="fs-icon-flex"><fs-icon icon="fs-24-tag" variant="primary"></fs-icon></div>
        <div class="promo-header-column">
          <fs-heading size="normal" level="2" variant="primary" class="promo-container-header">Access code</fs-heading>
          <div><fs-body-text size="normal" class="prefilled-access-code">${this.promotionalCode}</fs-body-text></div>
        </div>
        <div>
          <button class="fs-link-standalone fs-link-standalone--small fs-text-weight--semibold edit-button icon-edit-button" @click="${this.editAccessCodeClick}" aria-label="Edit Access Code">
            <fs-icon icon="fs-24-edit" description="Edit Access Code Button" size="small" class="fs-icon fs-icon--primary fs-icon--interactive"></fs-icon> Edit
          </button>
        </div>
      </div>
    </fs-container>`;
  }

  editAccessCodeForm() {
    return html` <fs-container border="" elevation="0" radius="large">
      <div class="editPrefillAccessCode">
        <div>
          <fs-text-field
            class="includes-error-message enter-access-code-field"
            id="promotionalCode"
            name="promotionalCode"
            type="text"
            value-missing-message="Please enter your access code"
            maxLength="8"
            minLength="8"
            too-short-message="Your access code needs to have 8 numbers"
            pattern-mismatch-message="Only numbers are allowed in this field"
            pattern="\\d{8}"
            @click="${this.accessCodeFieldClicked}"
            @input="${ev => this.changed('enteredAccessCodeInput', ev)}"
            value="${this.enteredAccessCodeInput}"
            required
            >Enter your access code
          </fs-text-field>
        </div>
        <div class="button-container">
          <fs-button class="fs-button cancel-button form-button" variant="secondary" @click="${this.cancelAccessCodeEditButtonClicked}" type="button"> Cancel </fs-button>
          <fs-button class="fs-button enter-button form-button" variant="primary" @click="${this.updateAccessCodeButtonClicked}" type="button"> Update access code </fs-button>
        </div>
      </div>
    </fs-container>`;
  }

  // Access Code Field Edit Options
  editAccessCodeClick(ev) {
    ev.preventDefault();
    stratum.publishTag(TagKey.ACCESS_CODE_EDIT_ACCESS_CODE);
    this.editAccessCodeInputFormAppears = true;
    this.promotionalCode = '';
    this.headerAprValue = '';
    this.clickAccessCodeTagSent = '';
    this.enteredAccessCodeInput = '';
  }

  updateAccessCodeButtonClicked(ev) {
    ev.preventDefault();
    stratum.publishTag(TagKey.ACCESS_CODE_SAVE_NEW_ACCESS_CODE);
    this.updateAccessCodeIfValid();
  }

  updateAccessCodeIfValid() {
    if (this.enteredAccessCodeInput.length >= 8) {
      this.userUpdatedAccessCode = this.enteredAccessCodeInput;
      this.promotionalCode = this.enteredAccessCodeInput;
      this.editAccessCodeInputFormAppears = '';
    }
  }

  cancelAccessCodeEditButtonClicked(ev) {
    ev.preventDefault();
    stratum.publishTag(TagKey.ACCESS_CODE_CANCEL_EDIT_ACCESS_CODE);
    const externalId = this.searchExternalId();
    if (this.userUpdatedAccessCode.length >= 8) {
      this.promotionalCode = this.userUpdatedAccessCode;
      this.editAccessCodeInputFormAppears = '';
    } else if (externalId) {
      this.setPromoCodeAndAprValue(externalId);
      this.editAccessCodeInputFormAppears = '';
    }
  }

  renderSSNField() {
    return html`
      <div class="form-field-connected-entry">
        <fs-text-field
          class="includes-error-message ssn-enter-field"
          id="tax"
          name="tax"
          type="text"
          value="${this.tax}"
          pattern-mismatch-message="Only numbers are allowed in this field"
          value-missing-message="Enter the last 4 digits of your Social Security Number"
          too-short-message="This field needs to have 4 numbers"
          minLength="4"
          maxLength="4"
          pattern="\\d{4}"
          @click="${this.ssnFieldClicked}"
          @input="${ev => this.changed('tax', ev)}"
          required
          helper-text="We only use this to confirm it matches our records"
          >Last 4 digits of your Social Security Number
        </fs-text-field>
      </div>
    `;
  }

  findMySpecialOfferButton() {
    return html`
      <div class="confirm-button">
        <fs-button class="form-button form-button-connected-entry" variant="primary" @click="${this.confirmButtonClicked}" type="submit"> Find my special offer </fs-button>
      </div>
    `;
  }

  // Determine which error logo 1/2: No match or system failure
  displayErrorConnectedEntryLogo(errorMessage) {
    if (errorMessage === OFFER_CONST.CONNECTED_ENTRY_CUSTOMER_OFFER_NULL_MESSAGE) {
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_ACCESS_CODE_2);
      return html`${connectedEntryNoMatch}`;
    }
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_ACCESS_CODE_3);
    return html`${connectedEntrySystemFailure}`;
  }

  // First Update when page loads
  async firstUpdated() {
    const externalId = this.searchExternalId();
    if (externalId) {
      this.setPromoCodeAndAprValue(externalId);
      if (this.promotionalCode && this.headerAprValue) {
        this.connectedEntryAccessCodePrefilledTag();
        this.routedWithExternalId = true;
      }
    }
    this.connectedEntryPageRenderedTag();
  }

  setPromoCodeAndAprValue(externalId) {
    this.promotionalCode = this.generatePromoCode(externalId);
    this.headerAprValue = this.generateAprRate(externalId);
  }

  searchExternalId() {
    const params = new URLSearchParams(window.location.search);
    let externalId = params.get(QUERY_PARAMS.EXTERNAL_ID);
    externalId = externalId !== null ? externalId : '';
    return externalId;
  }

  generatePromoCode(extId) {
    const listExternalId = extId.split('_');
    const accessCode = listExternalId[listExternalId.length - 2];
    const isNumeric = /^\d+$/.test(accessCode);
    return isNumeric && accessCode.length === 8 ? accessCode : '';
  }

  generateAprRate(extId) {
    const listExternalId = extId.split('_');
    let aprRate = listExternalId[listExternalId.length - 3];
    const isValidFormat = /^\d{1,2}d\d{2}$/.test(aprRate);
    if (isValidFormat) {
      aprRate = aprRate.replace('d', '.');
      return aprRate.length === 4 || aprRate.length === 5 ? aprRate : '';
    }
    return '';
  }

  changed(name, ev) {
    this[name] = ev?.target?.value.replace(/[^0-9-]/g, '');
  }

  // Search Offer flavors
  async searchOffer(ev) {
    ev.preventDefault();
    this.debug.log('searchOffer called upon form submit');
    this.promotionalCode = this.promotionalCode.replace('-', '');
    CustomerOffersService.offer = null;
    // 02/05/25 TDB below:
    // Confirm if these Analytics are needed - PA offers are no longer in use
    // HELM Access Code starts with 0 but are not PA offers
    trackAnalyticsEvent('click next', '', 'click', 'button');
    if (this.isPreApprovedOffer(this.promotionalCode)) {
      trackAnalyticsEvent('PA code used');
    }
    const offerDetails = await this.fetchCustomerOffers();
    const isOfferFound = offerDetails?.annualPercentRate;
    if (offerDetails && !isOfferFound) {
      this.searchOfferNoMatch();
    } else if (!offerDetails) {
      this.searchOfferCallFailure();
    } else {
      this.searchOfferMatch(offerDetails);
    }
  }

  isPreApprovedOffer(code) {
    return code.substring(0, 1) === '0';
  }

  async fetchCustomerOffers() {
    return CustomerOffersService.getCustomerOffers(
      {
        customerTaxpayerIdLastFourDigits: this.tax,
        promotionalCode: this.promotionalCode,
      },
      MatchingState.primaryNonSso,
    );
  }

  searchOfferCallFailure() {
    stratum.publishTag(TagKey.ACCESS_CODE_ACCESS_CODE_CALL_FAIL);
    this.errorHeading = OFFER_CONST.CONNECTED_ENTRY_CUSTOMER_OFFER_FAILED_HEADER;
    this.errorMessage = OFFER_CONST.CONNECTED_ENTRY_FAILED_MESSAGE;
    this.scrollToTop();
  }

  searchOfferNoMatch() {
    stratum.publishTag(TagKey.ACCESS_CODE_ACCESS_CODE_MATCH_UNSUCCESSFUL);
    this.debug.log('Get customer offer returned null offer');
    trackAnalyticsEvent('no match');
    this.errorMessage = OFFER_CONST.CONNECTED_ENTRY_CUSTOMER_OFFER_NULL_MESSAGE;
    this.errorHeading = OFFER_CONST.CONNECTED_ENTRY_NO_MATCH_HEADER;
    this.scrollToTop();
  }

  searchOfferMatch(offerDetails) {
    this.debug.log('Search for offer called and returned offer', offerDetails);
    SessionService.setCachedOffer(offerDetails);
    stratum.publishTag(TagKey.ACCESS_CODE_ACCESS_CODE_MATCH_SUCCESSFUL);
    this.errorMessage = '';
    this.loading = true;
    this.scrollToTop();
    stratum.publishTag(TagKey.VIEW_ACCESS_CODE);
    setTimeout(() => {
      this.goToKochavaOrSsoLogin();
    }, 1000);
  }

  // continue without access code button clicked
  clearOffer(ev) {
    ev.preventDefault();
    CustomerOffersService.removeOffer();
    stratum.publishTag(TagKey.ACCESS_CODE_CONNECTED_ENTRY__APPLY_WITHOUT_ACCESS_CODE);
    this.goToKochavaOrSsoLogin();
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  // route to CI login page
  goToSsoLogin() {
    // 02/05/25 TBD: Track Analytics needed?
    trackAnalyticsEvent(`clicked sso`);
    SsoService.goToSsoLoginLink();
  }

  goToKochavaOrSsoLogin() {
    const params = new URLSearchParams(window.location.search);
    const refiAllocation = params.get(QUERY_PARAMS.REFI_ALLOCATION);

    // Parse and validate the allocation parameter
    const parsedAllocation = refiAllocation ? ReturnCustValidationService.validateAndParseReturnCust(refiAllocation) : null;
    const isControlAllocation = parsedAllocation?.isCodelessEntryControl === true;

    // Check if this is a control allocation and the feature flag is toggled on
    if (isControlAllocation && this.isFeatureEnabled(CODELESS_ENTRY_CONST.FEATURE_TOGGLE)) {
      // Use KochavaService to handle the Kochava flow
      KochavaService.handleKochavaFlow(params, () => this.goToSsoLogin());
      return; // Exit the function to prevent immediate SSO execution.
    }

    // If the feature flag is off or not a control allocation,
    // we proceed directly to the SSO login as the original flow intended.
    this.goToSsoLogin();
  }

  isFeatureEnabled(featureKey) {
    if (!this.featureToggles) return false;

    const toggle = this.featureToggles.find(feature => feature.featureKey === featureKey);

    return toggle ? toggle.isEnabled : false;
  }

  // Tags Fired
  connectedEntryAccessCodePrefilledTag() {
    if (!this.connectedEntryAccessCodePrefilled) {
      stratum.publishTag(TagKey.ACCESS_CODE_CONNECTED_ENTRY__ACCESS_CODE_PREFILL_SUCCESSFUL);
      this.connectedEntryAccessCodePrefilled = true;
    }
  }

  connectedEntryPageRenderedTag() {
    if (!this.connectedEntryPageRendered) {
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_ACCESS_CODE);
      this.connectedEntryPageRendered = true;
    }
  }

  confirmButtonClicked() {
    if (!this.confirmButtonClickedTagSent) {
      stratum.publishTag(TagKey.ACCESS_CODE_CONNECTED_ENTRY__APPLY_WITH_ACCESS_CODE);
      this.confirmButtonClickedTagSent = true;
    }
  }

  ssnFieldClicked() {
    if (!this.clickSSNTagSent) {
      stratum.publishTag(TagKey.ACCESS_CODE_ADD_SSN);
      this.clickSSNTagSent = true;
    }
  }

  accessCodeFieldClicked() {
    if (!this.clickAccessCodeTagSent) {
      stratum.publishTag(TagKey.ACCESS_CODE_CHANGE_ACCESS_CODE);
      this.clickAccessCodeTagSent = true;
    }
  }
}

customElements.define('refi-offer', RefiOffer);
